import { useState, useEffect } from "react";
import { privateApi } from "../../../lib/privateApi";

const INITIAL_FORM = {
    full_name: "",
    email: "",
    passcode: "",
    nda_accepted: false,
    website: "",
};

function validateAccess(form) {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Your full name, please.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        e.email = "That email looks off.";
    if (!form.passcode.trim()) e.passcode = "Please enter your passcode.";
    if (!form.nda_accepted)
        e.nda_accepted = "NDA acknowledgment is required.";
    return e;
}

export function usePrivateAccessForm(navigate) {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [status, setStatus] = useState("checking"); // checking|idle|loading

    // If already authenticated, bounce to /private.
    useEffect(() => {
        let cancelled = false;
        privateApi
            .me()
            .then(() => {
                if (!cancelled) navigate("/private", { replace: true });
            })
            .catch(() => !cancelled && setStatus("idle"));
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const setField = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const v = validateAccess(form);
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        setStatus("loading");
        setSubmitError("");
        try {
            const res = await privateApi.access(form);
            if (res && res.status === "granted") {
                // Hard reload — same cookie-attach race mitigation as the
                // concierge login flow. The response body carries `email`
                // but we don't need to thread it through React state; the
                // /private route guard reads it from /me after reload.
                window.location.assign("/private");
            } else {
                // Honeypot silent-drop returns {status:"ok"} without a cookie.
                // Treat as an unexpected error to avoid false positives.
                setStatus("idle");
                setSubmitError("Credentials not recognized.");
            }
        } catch (err) {
            setStatus("idle");
            if (err.status === 429) {
                setSubmitError(
                    err.message ||
                        "Too many failed attempts. Please try again later.",
                );
            } else {
                setSubmitError("Credentials not recognized.");
            }
        }
    };

    return { form, errors, submitError, status, setField, onSubmit };
}
